// PLAY: quick play into the busiest room, continue on the last map, invite
// codes, the active rooms and recent maps. Offline: the maps on this device.

import { Button } from '../components/Button';
import { ColorText } from '../components/ColorText';
import { Icon } from '../components/Icon';
import { JoinCode } from '../components/JoinCode';
import { Levelshot } from '../components/Levelshot';
import { MapCard } from '../components/MapCard';
import { useApp } from '../context';
import { describeRules } from '../maps/mapMeta';
import { useMapEntries } from '../maps/useCatalog';
import { createRoomDialog, net, selectedMap, settings, tab } from '../state';
import { toggleFavorite } from './Maps';
import { OfflineNotice, RoomRow } from './Rooms';

export function HomeScreen() {
  const app = useApp();
  const s = settings.value;
  const n = net.value;
  const online = n.status === 'online';
  const entries = useMapEntries();
  const byName = new Map(entries.map((e) => [e.name, e]));
  // offline only maps on this device can be played
  const playable = (name: string) => !!byName.get(name) && (online ? !!byName.get(name)!.info : byName.get(name)!.local);
  const recent = s.recentMaps.filter((r) => playable(r.name)).map((r) => byName.get(r.name)!);
  const busiest = online ? n.rooms.find((r) => r.players > 0) : undefined;
  const hero = recent[0] ?? (busiest ? byName.get(busiest.map) : undefined) ?? entries.find((e) => playable(e.name));
  const activeRooms = n.rooms.filter((r) => r.players > 0).slice(0, 4);
  const playing = n.rooms.reduce((a, r) => a + r.players, 0);

  return (
    <div class="screen home-screen">
      <section class="hero" aria-label="Play">
        {hero && <Levelshot map={hero.name} eager class="hero-bg" />}
        <div class="hero-shade" />
        <div class="hero-content">
          <div class="eyebrow">{online ? `${n.serverName} · ${playing} playing` : n.status === 'connecting' ? 'Connecting…' : 'Offline'}</div>
          {hero ? (
            <>
              <h1 class="hero-title">{hero.longname ? <ColorText text={hero.longname} /> : hero.name}</h1>
              <div class="hero-meta">
                <span class="mono">{hero.name}</span>
                {hero.meta && hero.meta.runs.length > 0 && (
                  <span>
                    {hero.meta.runs.length} timerun{hero.meta.runs.length > 1 ? 's' : ''}
                  </span>
                )}
                {hero.meta && describeRules(hero.meta.rules).map((r) => <span key={r}>{r}</span>)}
              </div>
            </>
          ) : (
            <h1 class="hero-title">TJgame</h1>
          )}
          <div class="hero-actions">
            {online ? (
              <>
                <Button variant="primary" size="lg" icon="play" hint="ENTER" autoFocus onClick={() => app.quickPlay()} title="Join the busiest room with free slots">
                  Quick play
                </Button>
                {hero && recent[0] && (
                  <Button variant="secondary" size="lg" onClick={() => app.quickPlay(hero.name)}>
                    Continue on {hero.name}
                  </Button>
                )}
                <Button variant="ghost" size="lg" icon="users" onClick={() => (createRoomDialog.value = { map: hero?.name ?? null })}>
                  Create room
                </Button>
              </>
            ) : hero ? (
              <Button variant="primary" size="lg" icon="play" autoFocus onClick={() => app.playOffline(hero.name)}>
                Play offline
              </Button>
            ) : null}
            {hero && (
              <Button
                variant="ghost"
                size="lg"
                icon="info"
                onClick={() => {
                  selectedMap.value = hero.name;
                  tab.value = 'maps';
                }}
              >
                Details
              </Button>
            )}
          </div>
          {online && (
            <div class="hero-join">
              <span class="muted small">Got an invite code?</span>
              <JoinCode />
            </div>
          )}
        </div>
      </section>

      <div class="home-rows">
        {online ? (
          <section class="row-section">
            <header class="row-head">
              <h2 class="section-title">Active rooms</h2>
              <Button variant="ghost" size="sm" onClick={() => (tab.value = 'rooms')}>
                All rooms <Icon name="chevron" size={14} />
              </Button>
            </header>
            {activeRooms.length ? (
              <div class="room-list">
                {activeRooms.map((r) => (
                  <RoomRow key={r.id} room={r} />
                ))}
              </div>
            ) : (
              <p class="muted">Nobody is playing right now: start with Quick play and others will join you.</p>
            )}
          </section>
        ) : (
          <section class="row-section">
            <OfflineNotice />
          </section>
        )}

        {recent.length > 1 && (
          <section class="row-section">
            <header class="row-head">
              <h2 class="section-title">Recent maps</h2>
              <Button variant="ghost" size="sm" onClick={() => (tab.value = 'maps')}>
                All maps <Icon name="chevron" size={14} />
              </Button>
            </header>
            <div class="card-row">
              {recent.slice(0, 6).map((e) => (
                <MapCard key={e.name} entry={e} onSelect={() => (online ? app.quickPlay(e.name) : app.playOffline(e.name))} onToggleFavorite={() => toggleFavorite(e.name)} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
